import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map, shareReplay, tap } from 'rxjs/operators';
import { LocalStorageUtil } from '../utils/local-storage.util';
import { resolveBffUrl } from '../utils/bff-url-resolver.util';

const UAT_BFF_BASE_URL = 'https://bff-leads.cons.pp.cld.bancovw.com.br';

export interface PublicRuntimeConfig {
  requireSsoAuthentication: boolean;
  platformBaseUrl: string;
  bancoVwApiBaseUrl: string;
  bffUrl: string;
}

export interface RuntimeConfig {
  bffUrl: string;
  googleAnalyticsId: string;
  apiUrl: string;
  apiCdcUrl: string;
  apiQuickApprovalUrl: string;
  bancoVw: {
    apiBaseUrl: string;
    platformBaseUrl: string;
    platformLoginUrl: string;
    imageBaseUrl: string;
    defaultCarImage: string;
    platformSimulationUrl: string;
  };
  bancoVwUrlParts: {
    leadInfoPath: string;
    simulacaoPath: string;
    historicoSimulacoesPath: string;
  };
  external: {
    whatsappApi: string;
    whatsappUrl: string;
  };
  storage: {
    keyUser: string;
    keyToken: string;
    keySelectedDn: string;
  };
  production: boolean;
  enableDebugMode: boolean;
  logLevel: string;
  phase2: boolean;
}

type RuntimeWindow = Window & {
  __vwRuntimeConfig?: Pick<RuntimeConfig, 'enableDebugMode' | 'logLevel'>;
};

@Injectable({
  providedIn: 'root'
})

export class ConfigService {
  private config$: Observable<RuntimeConfig> | null = null;
  private cachedConfig: RuntimeConfig | null = null;
  private publicConfig$: Observable<PublicRuntimeConfig> | null = null;
  private cachedPublicConfig: PublicRuntimeConfig | null = null;

  constructor(private http: HttpClient) {}

  /**
   * Carrega configuração do BFF (que busca do AWS Parameter Store)
   * Inclui Authorization: Bearer quando houver token no localStorage (após OAuth).
   */
  loadConfig(): Observable<RuntimeConfig> {
    if (this.config$) {
      return this.config$;
    }

    if (this.isDebugEnabled()) {
      console.log('⚙️ [ConfigService] Carregando configuração de: /api/Config/frontend');
    }

    const headers = this.buildAuthHeaders();
    const backendUrl = resolveBffUrl(UAT_BFF_BASE_URL, true);
    this.config$ = this.http.get<RuntimeConfig>(`${backendUrl}/api/Config/frontend`, { headers }).pipe(
      tap(config => {
        this.storeRuntimeConfigSnapshot(config);
        if (this.isDebugEnabled()) {
          console.log('✅ [ConfigService] Configuração carregada do BFF');
        }
        this.cachedConfig = config;
      }),
      catchError(error => {
        const errorMsg = `[CRÍTICO] Falha ao carregar configuração do BFF em /api/Config/frontend. Detalhes: ${error.message}`;
        console.error('❌ [ConfigService]', errorMsg);
        return throwError(() => error);
      }),
      shareReplay(1) // Cache da requisição
    );

    return this.config$;
  }

  loadPublicConfig(): Observable<PublicRuntimeConfig> {
    if (this.publicConfig$) {
      return this.publicConfig$;
    }

    const publicBackendUrl = resolveBffUrl(UAT_BFF_BASE_URL, true);
    this.publicConfig$ = this.http.get<PublicRuntimeConfig>(`${publicBackendUrl}/api/Config/frontend/public`).pipe(
      tap(config => {
        this.cachedPublicConfig = config;
        console.log('✅ [ConfigService] Configuração pública carregada com sucesso:', `${publicBackendUrl}/api/Config/frontend/public`);
      }),
      catchError(error => {
        console.error('❌ [ConfigService] Falha ao carregar configuração pública de autenticação em:', `${publicBackendUrl}/api/Config/frontend/public`, error);
        return throwError(() => error);
      }),
      shareReplay(1)
    );

    return this.publicConfig$;
  }

  getPlatformLoginUrl(): Observable<string> {
    if (this.cachedPublicConfig?.platformBaseUrl) {
      return of(this.buildSsoLoginUrl(this.cachedPublicConfig.platformBaseUrl));
    }

    return this.loadPublicConfig().pipe(
      map(config => this.buildSsoLoginUrl(config.platformBaseUrl))
    );
  }

  getFallbackPlatformLoginUrl(): string {
    return this.buildSsoLoginUrl(
      this.cachedPublicConfig?.platformBaseUrl?.trim()
      || this.cachedConfig?.bancoVw?.platformBaseUrl?.trim()
      || ''
    );
  }

  private buildSsoLoginUrl(platformBaseUrl: string): string {
    const sanitizedUrl = (platformBaseUrl || '').trim();
    if (sanitizedUrl === '') {
      return '';
    }

    return `${sanitizedUrl.replace(/\/+$/, '')}/login`;
  }

  /**
   * Headers com Authorization: Bearer quando houver token no localStorage
   */
  private buildAuthHeaders(): HttpHeaders {
    let headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    try {
      const token = LocalStorageUtil.getAccessTokenOrDefault('');
      if (token && token.trim() !== '') {
        headers = headers.set('Authorization', `Bearer ${token}`);
      }
    } catch {
      // sem token — requisição segue sem Bearer
    }
    return headers;
  }

  /**
   * Nunca retorna null após loadConfig() ser executado com sucesso em main.ts
   */
  getConfig(): RuntimeConfig | null {
    if (!this.cachedConfig) {
      console.error('❌ [ConfigService] ERRO CRÍTICO: Configuração não carregada. loadConfig() foi chamado?');
      return null;
    }
    return this.cachedConfig;
  }

  /**
   * Retorna a configuração atual sem log de erro quando ainda não carregou.
   */
  getConfigIfLoaded(): RuntimeConfig | null {
    return this.cachedConfig;
  }

  /**
   * Retorna bffUrl disponível em memória, priorizando config privada e depois pública.
   */
  getBffUrlIfLoaded(): string | undefined {
    const privateBffUrl = this.cachedConfig?.bffUrl?.trim();
    if (privateBffUrl) {
      return privateBffUrl;
    }

    const publicBffUrl = this.cachedPublicConfig?.bffUrl?.trim();
    if (publicBffUrl) {
      return publicBffUrl;
    }

    return undefined;
  }

  /**
   * Retorna o flag phase2 de forma explícita, evitando ambiguidade de fallback por valor.
   */
  isPhase2Enabled(): boolean {
    return this.cachedConfig?.phase2 ?? false;
  }

  /**
   * Retorna o valor da configuração runtime pelo caminho passado como string.
   * Exemplo: getByPath<string>('bancoVw.platformLoginUrl').
   * Retorna undefined se a config não estiver carregada ou o caminho não existir.
   */
  getByPath<T>(path: string): T | undefined {
    const config = this.getConfigIfLoaded();
    if (!config) {
      return undefined;
    }
    return this.navigatePath(config as unknown, path) as T | undefined;
  }

  /**
   * Retorna o valor da configuração runtime e falha explicitamente quando ausente.
   */
  getRequiredByPath<T>(path: string): T {
    const config = this.getConfigIfLoaded();
    if (!config) {
      throw new Error(`Configuração runtime não carregada. Caminho solicitado: ${path}`);
    }

    const value = this.navigatePath(config as unknown, path);
    if (value === null || value === undefined || (typeof value === 'string' && value.trim() === '')) {
      throw new Error(`Parâmetro obrigatório ausente na configuração runtime: ${path}`);
    }

    return value as T;
  }

  /**
   * Retorna true se o modo debug estiver habilitado na configuração runtime.
   * Retorna false quando a config ainda não foi carregada.
   */
  isDebugEnabled(): boolean {
    return this.cachedConfig?.enableDebugMode === true;
  }

  private navigatePath(source: unknown, path: string): unknown {
    if (!source || !path || path.trim() === '') {
      return undefined;
    }

    return path
      .split('.')
      .reduce<unknown>((current, key) => {
        if (current !== null && current !== undefined && typeof current === 'object' && key in (current as Record<string, unknown>)) {
          return (current as Record<string, unknown>)[key];
        }
        return undefined;
      }, source);
  }

  private storeRuntimeConfigSnapshot(config: RuntimeConfig): void {
    if (typeof window === 'undefined') {
      return;
    }

    (window as RuntimeWindow).__vwRuntimeConfig = {
      enableDebugMode: config.enableDebugMode,
      logLevel: config.logLevel
    };
  }

}
